/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import adminServices from '../../../services/adminServices';

const INPUT_CLASS =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400';
const LOCKED_CLASS =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500 bg-gray-50 outline-none cursor-not-allowed';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Letters plus spaces, hyphen, apostrophe and dot (e.g. "Anne-Marie", "O'Brien", "Jr.").
const NAME_RE = /^[A-Za-z][A-Za-z\s.'-]*$/;
// Optional +, then 7-19 chars of digits / spaces / ( ) - .
const PHONE_RE = /^\+?[0-9][0-9\s()-]{6,18}$/;

const Label = ({ children, required }) => (
  <p className="text-xs font-medium text-gray-700 mb-1.5">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </p>
);

export default function AddAdminUserForm({ mode, data, userId, getList, toggle, snackBarControl }) {
  const isEdit = mode === 'edit';
  const [values, setValues] = useState({ firstName: '', lastName: '', email: '', contactPhone: '', password: '', roleId: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [formError, setFormError] = useState(null);

  // Assignable roles are the active, non-built-in roles whose layout is ADMIN -
  // every account created here runs on the admin shell.
  useEffect(() => {
    adminServices.getAssignableRoles()
      .then((res) => { if (res.ok) setRoles(res.data?.data || []); })
      .catch((error) => console.log(error, 'failed to [getAssignableRoles]'));
  }, []);

  useEffect(() => {
    if (isEdit && data) {
      setValues({
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        email: data.email || '',
        contactPhone: data.phone || '',
        password: '',
        roleId: data.role_id ? String(data.role_id) : '',
      });
    } else {
      setValues({ firstName: '', lastName: '', email: '', contactPhone: '', password: '', roleId: '' });
    }
    setErrors({});
    setFormError(null);
  }, [mode, data, isEdit]);

  // The create dropdown only lists assignable (active, non-built-in, ADMIN-layout)
  // roles. When editing, the account's current role may not be in that set
  // (e.g. a SUPER_ADMIN listed here) - merge it in so the select can show it.
  const roleOptions = React.useMemo(() => {
    if (isEdit && data?.role_id && !roles.some((r) => String(r.id) === String(data.role_id))) {
      return [
        { id: data.role_id, code: data.role_code, description: data.role_name || data.role_code },
        ...roles,
      ];
    }
    return roles;
  }, [roles, isEdit, data]);

  const set = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};

    const firstName = values.firstName.trim();
    if (!firstName) next.firstName = 'First name is required.';
    else if (firstName.length < 2) next.firstName = 'First name must be at least 2 characters.';
    else if (firstName.length > 50) next.firstName = 'First name is too long.';
    else if (!NAME_RE.test(firstName)) next.firstName = "First name can only contain letters, spaces, . ' and -.";

    const lastName = values.lastName.trim();
    if (lastName && !NAME_RE.test(lastName)) next.lastName = "Last name can only contain letters, spaces, . ' and -.";
    else if (lastName.length > 50) next.lastName = 'Last name is too long.';

    const phone = values.contactPhone.trim();
    if (phone && !PHONE_RE.test(phone)) next.contactPhone = 'Enter a valid phone number.';

    if (!values.roleId) next.roleId = 'Select a role.';

    if (!isEdit) {
      if (!values.email.trim()) next.email = 'Email is required.';
      else if (!EMAIL_RE.test(values.email.trim())) next.email = 'Enter a valid email address.';
      if (values.password.length < 8) next.password = 'Password must be at least 8 characters.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    setFormError(null);
    try {
      // Email is the login identifier, so it is only sent on create - the API
      // rejects an attempt to change it.
      const payload = isEdit
        ? {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            contactPhone: values.contactPhone.trim(),
            roleId: Number(values.roleId),
          }
        : {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            contactPhone: values.contactPhone.trim(),
            password: values.password,
            roleId: Number(values.roleId),
          };

      const response = isEdit
        ? await adminServices.putAdminUser(payload, userId)
        : await adminServices.postAdminUser(payload);

      if (response.ok) {
        snackBarControl(response.data?.message || (isEdit ? 'Admin user updated' : 'Admin user created'));
        getList();
        toggle();
      } else {
        setFormError(response.data?.message || 'Something went wrong.');
      }
    } catch (error) {
      console.log(error, 'failed to [saveAdminUser]');
      setFormError('Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-1 py-2">
      {formError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm text-red-600">{formError}</p>
        </div>
      )}

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <Label required>First Name</Label>
          <input type="text" value={values.firstName} onChange={set('firstName')} className={INPUT_CLASS} placeholder="First name" />
          {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
        </div>
        <div className="flex-1">
          <Label>Last Name</Label>
          <input type="text" value={values.lastName} onChange={set('lastName')} className={INPUT_CLASS} placeholder="Last name" />
          {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
        </div>
      </div>

      <div className="mb-4">
        <Label required={!isEdit}>Email</Label>
        <input
          type="email"
          value={values.email}
          onChange={set('email')}
          disabled={isEdit}
          className={isEdit ? LOCKED_CLASS : INPUT_CLASS}
          placeholder="name@company.com"
        />
        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        {isEdit && <p className="text-xs text-gray-400 mt-1">Email is the login identifier and cannot be changed here.</p>}
      </div>

      <div className="mb-4">
        <Label>Phone</Label>
        <input type="text" value={values.contactPhone} onChange={set('contactPhone')} className={INPUT_CLASS} placeholder="Optional" />
        {errors.contactPhone && <p className="text-xs text-red-500 mt-1">{errors.contactPhone}</p>}
      </div>

      {!isEdit && (
        <div className="mb-4">
          <Label required>Temporary Password</Label>
          <input type="text" value={values.password} onChange={set('password')} className={INPUT_CLASS} placeholder="At least 8 characters" />
          {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
          <p className="text-xs text-gray-400 mt-1">Emailed to the user along with their login link.</p>
        </div>
      )}

      <div className="mb-4">
        <Label required>Role</Label>
        <select value={values.roleId} onChange={set('roleId')} className={`${INPUT_CLASS} cursor-pointer`}>
          <option value="">Select a role…</option>
          {roleOptions.map((r) => (
            <option key={r.id} value={r.id}>{r.description || r.code}</option>
          ))}
        </select>
        {errors.roleId && <p className="text-xs text-red-500 mt-1">{errors.roleId}</p>}
        {roleOptions.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">
            No assignable roles yet. Create one under Roles &amp; Permissions with layout ADMIN.
          </p>
        )}
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl mb-5">
        <p className="text-[12.5px] text-blue-800">
          Accounts created here use the <strong>admin layout</strong>. Which pages the role can open
          is controlled from Roles &amp; Permissions.
        </p>
      </div>

      <div className="flex justify-end gap-2.5">
        <button
          type="button"
          onClick={toggle}
          className="border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
        >
          Cancel
        </button>
        <LoadingButton
          loading={saving}
          variant="contained"
          onClick={handleSubmit}
          sx={{
            background: '#2563eb', borderRadius: '7px', textTransform: 'none',
            fontSize: '13px', fontWeight: 600, px: '20px',
            '&:hover': { background: '#1d4ed8' },
          }}
        >
          {isEdit ? 'Save Changes' : 'Create User'}
        </LoadingButton>
      </div>
    </div>
  );
}
